/**
 * OpenClaw WebSocket chat hook.
 * Translates between OpenClaw's protocol (req/res/event frames)
 * and the Tesseract chat store's expected events.
 * Drop-in replacement for useChatWebSocket.
 */
import { useRef, useCallback, useEffect } from 'react';
import { useChatStore } from '../store/chatStore';
import { TOOL_LABELS } from '../constants/chatToolLabels';

let requestIdCounter = 0;
function nextId() {
  return `req-${++requestIdCounter}-${Date.now()}`;
}

export interface ChatHookConfig {
  userEmail?: string;
}

export const useOpenClawChat = (config?: ChatHookConfig) => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stable session key per user — reuses the same OpenClaw session across page loads.
  // Compaction handles long-lived sessions automatically.
  // Use the agent:etl-bot: prefix so chat routes to the ETL Bot agent.
  // Starts empty — set once email is available (on mount or hello-ok).
  const sessionKeyRef = useRef<string>(
    config?.userEmail
      ? `agent:etl-bot:tesseract-web-${config.userEmail}`
      : ''
  );
  const connectedRef = useRef(false);
  const userEmailRef = useRef(config?.userEmail || '');
  // Track whether the first message has included the auth prefix
  const authSentRef = useRef(false);

  // Keep email ref in sync with prop changes
  userEmailRef.current = config?.userEmail || '';

  // Sync session key when email becomes available after mount.
  // Fixes race condition: if hello-ok fires before authService resolves the email,
  // sessionKeyRef stays empty and sendMessage silently drops all messages.
  if (config?.userEmail && !sessionKeyRef.current) {
    sessionKeyRef.current = `agent:etl-bot:tesseract-web-${config.userEmail}`;
  }

  const {
    startStream,
    appendStreamText,
    setToolStatus,
    addToolCall,
    addActivity,
    updateLastActivity,
    completeLastActivity,
    finalizeMessage,
    setError,
  } = useChatStore();

  const sendFrame = useCallback((frame: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(frame));
    }
  }, []);

  const handleEvent = useCallback((data: Record<string, unknown>) => {
    const event = data.event as string;
    const payload = data.payload as Record<string, unknown> | undefined;
    if (!payload) return;

    if (event === 'connect.challenge') {
      // Respond with connect request — token comes from dedicated env var only
      const token = (import.meta.env.VITE_OPENCLAW_TOKEN ?? '').trim();
      sendFrame({
        type: 'req',
        id: nextId(),
        method: 'connect',
        params: {
          minProtocol: 3,
          maxProtocol: 3,
          client: {
            id: 'webchat-ui',
            displayName: 'Tesseract Chat Widget',
            version: '1.0.0',
            platform: 'browser',
            mode: 'webchat',
          },
          auth: { token },
          scopes: ['operator.write', 'operator.admin'],
        },
      });
      return;
    }

    if (event === 'chat') {
      const state = payload.state as string;
      const message = payload.message as Record<string, unknown> | undefined;

      if (state === 'delta' && message) {
        const content = message.content as Array<Record<string, unknown>> | undefined;
        if (content) {
          for (const block of content) {
            if (block.type === 'text' && typeof block.text === 'string') {
              setToolStatus(null);
              appendStreamText(block.text);
            }
          }
        }
      } else if (state === 'final' && message) {
        const content = message.content as Array<Record<string, unknown>> | undefined;
        let fullText = '';
        if (content) {
          for (const block of content) {
            if (block.type === 'text' && typeof block.text === 'string') {
              fullText += block.text;
            }
          }
        }
        completeLastActivity('done');
        finalizeMessage(fullText || undefined);
      } else if (state === 'error') {
        setError((payload.errorMessage as string) || 'An error occurred');
      } else if (state === 'aborted') {
        finalizeMessage();
      }
      return;
    }

    if (event === 'agent') {
      const stream = payload.stream as string;
      const agentData = payload.data as Record<string, unknown> | undefined;
      if (!agentData) return;

      if (stream === 'tool') {
        const phase = agentData.phase as string;
        const name = agentData.name as string;

        if (phase === 'start') {
          setToolStatus(null);
          addActivity(TOOL_LABELS[name] || name);
        } else if (phase === 'end') {
          const successful = agentData.successful !== false;
          completeLastActivity(successful ? 'done' : 'error');
          addToolCall(name, successful);
        }
      } else if (stream === 'assistant') {
        const text = agentData.text as string | undefined;
        if (text) {
          setToolStatus(null);
          appendStreamText(text);
        }
      } else if (stream === 'lifecycle') {
        const phase = agentData.phase as string;
        if (phase === 'start') {
          setToolStatus('Thinking...');
        }
      }
      return;
    }
  }, [appendStreamText, setToolStatus, addToolCall, addActivity, completeLastActivity, finalizeMessage, setError, sendFrame]);

  const handleResponse = useCallback((data: Record<string, unknown>) => {
    if (data.ok === true) {
      const payload = data.payload as Record<string, unknown> | undefined;
      if (payload?.type === 'hello-ok') {
        connectedRef.current = true;
        // Set session key from current email (may not have been available at mount)
        const email = userEmailRef.current;
        if (email) {
          sessionKeyRef.current = `agent:etl-bot:tesseract-web-${email}`;
        }
      }
    } else if (data.ok === false) {
      const error = data.error as Record<string, unknown> | undefined;
      const msg = error?.message || 'Connection error';
      setError(msg as string);
    }
  }, [setError]);

  const connect = useCallback(() => {
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) return;

    try {
      const OPENCLAW_URL = (import.meta.env.VITE_OPENCLAW_URL ?? '').trim();
      let wsUrl: string;

      if (OPENCLAW_URL) {
        // Parse the configured URL
        const url = new URL(OPENCLAW_URL);
        const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${wsProtocol}//${url.host}`;
      } else {
        wsUrl = 'ws://localhost:18789';
      }

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {};

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'event') {
            handleEvent(data);
          } else if (data.type === 'res') {
            handleResponse(data);
          }
        } catch (err) {
          if (import.meta.env.DEV) console.error('Failed to parse OpenClaw message:', err);
        }
      };

      ws.onerror = () => {
        if (import.meta.env.DEV) console.error('OpenClaw WebSocket error');
      };

      ws.onclose = (event) => {
        connectedRef.current = false;
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 3000);
        }
      };
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to create OpenClaw WebSocket:', err);
    }
  }, [handleEvent, handleResponse]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Component unmounting');
      wsRef.current = null;
    }
    connectedRef.current = false;
  }, []);

  const sendMessage = useCallback((text: string) => {
    if (!sessionKeyRef.current) {
      setError('Chat session not ready — please wait for connection...');
      return;
    }
    if (wsRef.current?.readyState === WebSocket.OPEN && connectedRef.current) {
      // On the first message, prepend the auth directive so the LLM
      // authenticates the user inline (no separate greeting message).
      let message = text;
      const email = userEmailRef.current;
      if (email && !authSentRef.current) {
        authSentRef.current = true;
        message = `[web-frontend-connect:${email}] ${text}`;
      }

      sendFrame({
        type: 'req',
        id: nextId(),
        method: 'chat.send',
        params: {
          sessionKey: sessionKeyRef.current,
          message,
          deliver: true,
          idempotencyKey: `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        },
      });
    } else {
      setError('Message not sent — chat is reconnecting...');
    }
  }, [sendFrame]);

  const clearHistory = useCallback(() => {
    // Clear UI and reset the server-side session in-place (same key, fresh context).
    authSentRef.current = false;
    useChatStore.getState().clearMessages();
    if (sessionKeyRef.current) {
      sendFrame({
        type: 'req',
        id: nextId(),
        method: 'sessions.reset',
        params: { key: sessionKeyRef.current },
      });
    }
  }, [sendFrame]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connect,
    disconnect,
    sendMessage,
    clearHistory,
    isConnected: connectedRef.current,
  };
};
